import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const mismatch = password && confirm && password !== confirm;
  const tooShort = password && password.length < 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Missing token in URL"); return; }
    if (mismatch || tooShort) return;
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated — please log in with your new password.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset link invalid or expired");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Header />
      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md bg-white border border-[#e2e8f0] rounded-lg p-8" data-testid="reset-password-card">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#2563eb]" />
            <h1 className="font-display font-bold text-2xl text-[#0f1b2d]">Set a new password</h1>
          </div>
          {!token ? (
            <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800" data-testid="reset-no-token">
              This page requires a valid reset link. Please use the link from your email or <Link to="/forgot-password" className="font-semibold underline">request a new one</Link>.
            </div>
          ) : (
            <>
              <p className="mt-1 text-sm text-[#475569]">Enter your new password below. The link expires 1 hour after you requested it.</p>
              <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="reset-form">
                <div>
                  <label className="text-sm font-semibold text-[#0f1b2d]">New password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    data-testid="reset-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 w-full border border-[#e2e8f0] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20"
                  />
                  {tooShort && <div className="mt-1 text-xs text-red-600">Must be at least 8 characters.</div>}
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#0f1b2d]">Confirm new password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    data-testid="reset-confirm"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1.5 w-full border border-[#e2e8f0] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20"
                  />
                  {mismatch && <div className="mt-1 text-xs text-red-600">Passwords don&apos;t match.</div>}
                </div>
                <button
                  type="submit"
                  disabled={submitting || mismatch || tooShort}
                  data-testid="reset-submit-btn"
                  className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold py-2.5 rounded-md transition-all"
                >
                  {submitting ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
